import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import AdminLayout, { VIOLET, timeGreeting } from '@/components/AdminLayout';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
    Users, ClipboardList, CheckCircle2, AlertTriangle, ArrowRight,
    Building2, ClipboardCheck, FileText, BookOpen, Plus, TrendingUp, TrendingDown,
    Award, Mail, MessageSquare, ClipboardX, Activity, ChevronDown,
} from 'lucide-react';

const TREND_COLORS = {
    up: { bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
    down: { bg: 'rgba(239,68,68,0.12)', text: '#EF4444' },
    flat: { bg: 'rgba(16,185,129,0.12)', text: '#10B981' },
};

export default function AdminDashboard() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [metrics, setMetrics] = useState(null);
    const [activity, setActivity] = useState([]);
    const [totals, setTotals] = useState({ inspectors: 0, clients: 0 });
    const [badges, setBadges] = useState(null);

    useEffect(() => {
        Promise.all([
            api.get('/admin/dashboard').then((r) => setMetrics(r.data)),
            api.get('/admin/activity?limit=15').then((r) => setActivity(r.data)),
            api.get('/admin/users').then((r) => {
                const us = r.data || [];
                setTotals({
                    inspectors: us.filter((u) => u.role === 'inspector' && u.active).length,
                    clients: us.filter((u) => ['client', 'provider'].includes(u.role) && u.active).length,
                });
            }),
            api.get('/admin/dashboard/badges').then((r) => setBadges(r.data)).catch(() => setBadges(null)),
        ]);
    }, []);

    if (!metrics) {
        return <AdminLayout><div className="p-12 text-center text-gray-500">Cargando...</div></AdminLayout>;
    }

    const greeting = timeGreeting();
    const firstName = user?.name?.split(' ')[0] || 'Admin';

    const pendingCount = metrics.pending + metrics.assigned + metrics.in_progress + metrics.in_review;
    const urgentCount = metrics.in_review;

    const kpis = [
        {
            label: 'Auditores Activos', value: totals.inspectors, icon: Users,
            trend: 'flat', trendLabel: '+0 esta semana', highlight: true,
            to: '/admin/inspectors',
        },
        {
            label: 'Auditorías Pendientes', value: pendingCount, icon: ClipboardList,
            trend: urgentCount > 0 ? 'down' : 'flat', trendLabel: urgentCount > 0 ? `${urgentCount} urgentes` : 'sin urgentes',
            to: '/admin/inspections?filter=pending_all',
        },
        {
            label: 'Completadas este mes', value: metrics.approved + metrics.rejected, icon: CheckCircle2,
            trend: 'flat', trendLabel: `${metrics.approval_rate}% aprobación`,
            to: '/admin/inspections?filter=completed_month',
        },
        {
            label: 'Observaciones Abiertas', value: metrics.in_review, icon: AlertTriangle,
            trend: 'flat', trendLabel: '+0 esta semana',
            to: '/admin/critical-findings',
        },
    ];

    const quickActions = [
        { label: 'Nuevo Cliente', icon: Building2, to: '/admin/clients', color: '#7C6BEF' },
        { label: 'Nuevo Distintivo', icon: ClipboardCheck, to: '/admin/certifications', color: '#10B981' },
        { label: 'Ver Normativas', icon: FileText, to: '/admin/settings/certifications', color: '#9D8DF1' },
        { label: 'Gestionar Catálogo', icon: BookOpen, to: '/admin/catalog', color: '#E08B3C' },
    ];

    return (
        <AdminLayout title="Panel Principal">
            {/* Hero greeting */}
            <div data-testid="admin-hero-greeting" className="relative overflow-hidden p-10 mb-8" style={{ background: `linear-gradient(135deg, ${VIOLET} 0%, #5F4FD8 100%)` }}>
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.08] pointer-events-none"
                    style={{
                        backgroundImage: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='104' viewBox='0 0 120 104'><polygon points='60,2 118,32 118,72 60,102 2,72 2,32' fill='none' stroke='white' stroke-width='2'/></svg>")`,
                        backgroundSize: '120px 104px',
                    }}
                />
                <div className="relative">
                    <div className="text-[11px] uppercase tracking-[0.25em] font-bold text-white/70 mb-3 inline-flex items-center gap-2">
                        <span className="inline-block">⚡</span> Panel Principal
                    </div>
                    <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tight text-white mb-2">
                        {greeting},<span className="ml-3">{firstName}</span> <span className="inline-block">👋</span>
                    </h1>
                    <p className="text-white/80">Aquí tienes el resumen de actividad de hoy.</p>
                </div>
            </div>

            {/* System health traffic-light (Iter 97) */}
            <SystemHealthWidget />

            {/* Alert badges (Iter 48) */}
            {badges && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="admin-alert-badges">
                    <AlertBadge testId="badge-audits-due" label="Auditorías por atender" description="Vencidas o próximas (7 días)" icon={ClipboardX} count={badges.audits_due?.count || 0} to={badges.audits_due?.to} />
                    <AlertBadge testId="badge-certs-expiring" label="Distintivos por vencer" description="Expiran en los próximos 30 días" icon={Award} count={badges.certs_expiring?.count || 0} to={badges.certs_expiring?.to} />
                    <AlertBadge testId="badge-emails-failed" label="Correos fallidos" description="Últimas 48 horas" icon={Mail} count={badges.emails_failed_48h?.count || 0} to={badges.emails_failed_48h?.to} />
                    <AlertBadge testId="badge-contacts-unanswered" label="Contactos sin responder" description="Sin acción · más de 48 h" icon={MessageSquare} count={badges.contacts_unanswered_48h?.count || 0} to={badges.contacts_unanswered_48h?.to} />
                </div>
            )}

            {/* KPI Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                {kpis.map((k) => (
                    <KpiCard key={k.label} {...k} />
                ))}
            </div>

            {/* Two columns: Activity + Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Activity feed */}
                <div className="lg:col-span-2 border" style={{ borderColor: 'var(--border, #E5E7EB)', background: 'var(--card-bg, #FFFFFF)' }}>
                    <div className="px-6 py-5 flex items-center justify-between border-b" style={{ borderColor: 'inherit' }}>
                        <h2 className="font-display text-lg font-bold">Actividad reciente</h2>
                        <Link to="/admin/certifications" className="text-xs font-bold inline-flex items-center gap-1 hover:underline" style={{ color: VIOLET }}>
                            Ver todo <ArrowRight className="w-3 h-3" />
                        </Link>
                    </div>
                    {activity.length === 0 ? (
                        <div className="p-12 text-center text-gray-500 text-sm">Sin actividad aún.</div>
                    ) : (
                        <div className="divide-y" style={{ borderColor: 'inherit' }}>
                            {activity.slice(0, 8).map((a) => <ActivityRow key={a.id} a={a} />)}
                        </div>
                    )}
                </div>

                {/* Quick actions */}
                <div className="border p-6" style={{ borderColor: 'var(--border, #E5E7EB)', background: 'var(--card-bg, #FFFFFF)' }}>
                    <h2 className="font-display text-lg font-bold mb-5">Acciones rápidas</h2>
                    <div className="space-y-3">
                        {quickActions.map((q) => {
                            const Icon = q.icon;
                            return (
                                <button
                                    key={q.label}
                                    onClick={() => navigate(q.to)}
                                    data-testid={`quick-${q.label.toLowerCase().replace(/\s/g, '-')}`}
                                    className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-transparent hover:border-current transition-colors group"
                                    style={{ background: `${q.color}11` }}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 flex items-center justify-center" style={{ background: `${q.color}22`, color: q.color }}>
                                            <Icon className="w-5 h-5" />
                                        </div>
                                        <span className="font-bold text-sm">{q.label}</span>
                                    </div>
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" style={{ color: q.color }} />
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </AdminLayout>
    );
}

function AlertBadge({ label, description, icon: Icon, count, to, testId }) {
    const active = count > 0;
    // Ámbar cuando hay pendientes, verde/neutro cuando 0.
    const color = active ? '#F59E0B' : '#10B981';
    const bg = active ? 'rgba(245, 158, 11, 0.12)' : 'rgba(16, 185, 129, 0.08)';
    const body = (
        <div
            className="p-4 border transition-all hover:-translate-y-0.5 hover:shadow-md cursor-pointer flex items-start gap-3"
            style={{ borderColor: active ? color : 'var(--border, #E5E7EB)', background: bg }}
            data-testid={testId}
        >
            <div className="w-9 h-9 flex items-center justify-center shrink-0" style={{ background: `${color}22`, color }}>
                <Icon className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                    <span className="font-black text-2xl leading-none" style={{ color }} data-testid={`${testId}-count`}>{count}</span>
                    <ArrowRight className="w-3.5 h-3.5 opacity-40" />
                </div>
                <div className="text-[11px] uppercase tracking-wider font-bold mt-1.5 truncate">{label}</div>
                <div className="text-[10px] text-gray-500 mt-0.5 truncate">{description}</div>
            </div>
        </div>
    );
    return to ? <Link to={to} className="block">{body}</Link> : body;
}

function KpiCard({ label, value, icon: Icon, trend, trendLabel, highlight, to }) {    const tc = TREND_COLORS[trend] || TREND_COLORS.flat;
    const TrendIcon = trend === 'down' ? TrendingDown : TrendingUp;
    const clickable = Boolean(to);
    const content = (
        <div
            className={`p-6 transition-all ${clickable ? 'hover:-translate-y-1 hover:shadow-xl cursor-pointer' : 'hover:-translate-y-0.5'}`}
            style={highlight
                ? { background: `linear-gradient(135deg, ${VIOLET} 0%, #5F4FD8 100%)`, color: 'white' }
                : { background: 'var(--card-bg, #FFFFFF)', border: '1px solid var(--border, #E5E7EB)' }
            }
            data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}
        >
            <div className="flex items-start justify-between mb-6">
                <div className={`w-11 h-11 flex items-center justify-center ${highlight ? 'bg-white/15' : ''}`}
                    style={!highlight ? { background: 'rgba(124,107,239,0.12)', color: VIOLET } : {}}>
                    <Icon className={`w-5 h-5 ${highlight ? 'text-white' : ''}`} />
                </div>
                <div
                    className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-bold"
                    style={highlight
                        ? { background: 'rgba(255,255,255,0.18)', color: 'white' }
                        : { background: tc.bg, color: tc.text }
                    }
                >
                    <TrendIcon className="w-3 h-3" /> {trendLabel}
                </div>
            </div>
            <div className="font-display text-5xl font-black leading-none mb-2" style={{ letterSpacing: '-0.03em' }}>{value}</div>
            <div className={`text-sm flex items-center justify-between ${highlight ? 'text-white/90' : 'text-gray-600 dark:text-gray-400'}`}>
                <span>{label}</span>
                {clickable && <ArrowRight className="w-3.5 h-3.5 opacity-60" />}
            </div>
        </div>
    );
    return clickable ? <Link to={to} className="block">{content}</Link> : content;
}

const COLOR_MAP = {
    violet: '#7C6BEF', blue: '#3B82F6', green: '#10B981', orange: '#E08B3C',
};

function timeAgo(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'ahora';
    if (diff < 3600) return `hace ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)} h`;
    if (diff < 86400 * 30) return `hace ${Math.floor(diff / 86400)} d`;
    if (diff < 86400 * 365) return `hace ${Math.floor(diff / (86400 * 30))} meses`;
    return d.toLocaleDateString('es');
}

function ActivityRow({ a }) {
    const c = COLOR_MAP[a.color] || '#7C6BEF';
    const initials = (a.title || 'A').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
    const content = (
        <div className="px-6 py-4 flex items-start gap-4 hover:bg-black/[0.02] dark:hover:bg-white/5 transition-colors">
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-xs shrink-0" style={{ background: c }}>
                {initials}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm leading-tight">
                    <span className="font-bold">{a.title}</span>{' '}
                    <span className="text-gray-600 dark:text-gray-400">{a.subtitle}</span>
                </div>
                {a.description && (
                    <div className="text-xs text-gray-500 mt-1 truncate">{a.description}</div>
                )}
            </div>
            <div className="text-xs text-gray-500 shrink-0">{timeAgo(a.at)}</div>
        </div>
    );
    if (a.link) {
        return (
            <Link to={a.link} data-testid={`activity-${a.id}`} className="block cursor-pointer">
                {content}
            </Link>
        );
    }
    return <div data-testid={`activity-${a.id}`}>{content}</div>;
}

// Iter 97 · Compact system-health traffic light. Polls /api/health/full and
// shows a green/amber/red status the admin can glance at; click to expand the
// per-check detail (DB ping, read latency, master-doc size). Fails soft: if the
// endpoint is unreachable it shows red "No disponible" rather than breaking.
const HEALTH_COLORS = {
    ok: { dot: '#10B981', text: '#10B981', label: 'Operativo' },
    degraded: { dot: '#E08B3C', text: '#E08B3C', label: 'Degradado' },
    down: { dot: '#EF4444', text: '#EF4444', label: 'No disponible' },
};

function SystemHealthWidget() {
    const [health, setHealth] = useState(null);
    const [state, setState] = useState('loading'); // loading | ok | degraded | down
    const [open, setOpen] = useState(false);

    const load = React.useCallback(() => {
        api.get('/health/full')
            .then((r) => { setHealth(r.data); setState(r.data?.status === 'ok' ? 'ok' : 'degraded'); })
            .catch(() => { setHealth(null); setState('down'); });
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 60000); // refresh every 60s
        return () => clearInterval(t);
    }, [load]);

    const c = HEALTH_COLORS[state] || HEALTH_COLORS.down;
    const checks = health?.checks || [];

    return (
        <div className="mb-6" data-testid="system-health-widget">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="system-health-toggle"
                className="w-full sm:w-auto inline-flex items-center gap-3 px-4 py-2.5 border bg-white dark:bg-white/5 hover:bg-black/[0.02] dark:hover:bg-white/10 transition-colors"
                style={{ borderColor: 'var(--border)' }}
            >
                <Activity className="w-4 h-4 text-gray-500 shrink-0" />
                <span className="text-[11px] uppercase tracking-wider font-bold text-gray-500">Estado del sistema</span>
                <span className="inline-flex items-center gap-2">
                    <span className="relative flex h-2.5 w-2.5">
                        {state !== 'down' && (
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full opacity-60" style={{ background: c.dot }} />
                        )}
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: c.dot }} data-testid="system-health-dot" />
                    </span>
                    <span className="text-sm font-bold" style={{ color: c.text }} data-testid="system-health-status">
                        {state === 'loading' ? 'Verificando…' : c.label}
                    </span>
                </span>
                {typeof health?.uptime_seconds === 'number' && (
                    <span className="hidden sm:inline text-xs text-gray-400">· uptime {formatUptime(health.uptime_seconds)}</span>
                )}
                <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="mt-2 border bg-white dark:bg-white/5 divide-y" style={{ borderColor: 'var(--border)' }} data-testid="system-health-detail">
                    {state === 'down' && (
                        <div className="px-4 py-3 text-sm text-red-600">
                            No se pudo obtener el estado del sistema. Reintentando cada 60 s…
                        </div>
                    )}
                    {checks.map((ck) => (
                        <div key={ck.name} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`health-check-${ck.name}`}>
                            <div className="flex items-center gap-2 min-w-0">
                                <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ background: ck.ok ? '#10B981' : '#EF4444' }} />
                                <span className="text-sm font-medium truncate">{HEALTH_CHECK_LABELS[ck.name] || ck.name}</span>
                            </div>
                            <div className="text-xs text-gray-500 shrink-0 tabular-nums">
                                {ck.ms} ms{ck.threshold_ms ? ` / ${ck.threshold_ms}` : ''}
                                {typeof ck.size_kb === 'number' && ` · ${ck.size_kb} KB`}
                            </div>
                        </div>
                    ))}
                    <div className="px-4 py-2 flex justify-end">
                        <button type="button" onClick={load} data-testid="system-health-refresh" className="text-xs font-bold" style={{ color: VIOLET }}>
                            Actualizar ahora
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const HEALTH_CHECK_LABELS = {
    db_ping: 'Conexión a base de datos',
    db_read: 'Lectura de datos',
    master_template: 'Master Checklist (tamaño/lectura)',
};

function formatUptime(s) {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m`;
    if (s < 86400) return `${Math.floor(s / 3600)}h`;
    return `${Math.floor(s / 86400)}d`;
}
